<pr-modal clazz="ged-picker-modal" display="neutral" #modalGed>
  <div class="ged-picker">
    <div class="ged-picker__head">
      <span class="ged-picker__head-icon">
        <pr-icon name="folder_open" [width]="24"></pr-icon>
      </span>
      <div class="ged-picker__titles">
        <h4>{{ title }}</h4>
        @if (subtitle) {
          <p>{{ subtitle }}</p>
        }
      </div>
      <button
        type="button"
        class="ged-picker__close"
        aria-label="Fermer"
        (click)="close()"
      >
        <pr-icon name="close" [width]="20"></pr-icon>
      </button>
    </div>

    <div class="ged-picker__body">
      <div class="ged-picker__list-pane">
        <div class="ged-picker__toolbar">
          <pr-search-input
            class="ged-picker__search"
            [searchValue]="searchValue"
            (searchValueChange)="onSearchChange($event)"
            placeholder="Rechercher par nom, titulaire ou type"
          >
          </pr-search-input>

          @if (canFilterByOwner) {
            <div
              class="ged-picker__scope"
              role="group"
              aria-label="Filtrer par titulaire"
            >
              <button
                type="button"
                [class.is-active]="scope === 'owner'"
                (click)="setScope('owner')"
              >
                {{ stepOwnerLabel }}
              </button>
              <button
                type="button"
                [class.is-active]="scope === 'all'"
                (click)="setScope('all')"
              >
                Tous les titulaires
              </button>
            </div>
          }
        </div>

        @if (loading) {
          <pr-loader></pr-loader>
        } @else {
          <div class="ged-picker__col-head">
            <span>{{ countLabel }}</span>
            <span>Aperçu</span>
          </div>

          <div class="ged-picker__rows">
            @if (!gedDocuments.length) {
              <div class="ged-picker__empty">
                <b>Aucun document de ce type dans la GED</b>
                Fermez cette fenêtre pour importer un fichier depuis votre
                ordinateur.
              </div>
            } @else if (!filteredDocuments.length) {
              <div class="ged-picker__empty">
                <b>Aucun document ne correspond</b>
                @if (canFilterByOwner && scope === 'owner') {
                  Essayez « Tous les titulaires », ou modifiez votre recherche.
                } @else {
                  Modifiez votre recherche.
                }
              </div>
            }

            @for (doc of displayedDocuments; track doc.id) {
              <div
                class="ged-picker__row"
                [class.is-selected]="isSelected(doc)"
                [class.is-unavailable]="isUnavailable(doc)"
                tabindex="0"
                (click)="onRowClick(doc)"
                (keydown.enter)="onRowClick(doc)"
                (keydown.space)="onRowClick(doc)"
              >
                <span class="ged-picker__tile">
                  <pr-icon
                    [name]="
                      doc.document === 'PASSEPORT' ? 'menu_book' : 'badge'
                    "
                    [width]="20"
                  ></pr-icon>
                </span>

                <span class="ged-picker__main">
                  <span class="ged-picker__label">
                    <span class="ged-picker__label-text">{{ label(doc) }}</span>
                    @for (tag of tags(doc); track tag.label) {
                      <span
                        class="ged-picker__chip"
                        [class.is-success]="tag.level === 'success'"
                        [class.is-danger]="tag.level === 'danger'"
                        [class.is-warning]="tag.level === 'warning'"
                        >{{ tag.label }}</span
                      >
                    }
                  </span>
                  <span class="ged-picker__meta">
                    <span
                      class="ged-picker__who"
                      [class.is-empty]="hasNoOwner(doc)"
                      >{{ ownersLabel(doc) }}</span
                    >
                    <span class="ged-picker__sep">·</span>{{ typeLabel(doc) }}
                    @if (fileCountLabel(doc)) {
                      <span class="ged-picker__sep">·</span
                      >{{ fileCountLabel(doc) }}
                    }
                    @if (formatDate(doc.dateCreated)) {
                      <span class="ged-picker__sep">·</span>ajouté le
                      {{ formatDate(doc.dateCreated) }}
                    }
                  </span>
                </span>

                @if (formatDate(doc.dateExpired)) {
                  <span
                    class="ged-picker__expiry"
                    [class.is-expired]="isExpired(doc)"
                  >
                    <span class="ged-picker__expiry-label">Expire le</span>
                    <span class="ged-picker__expiry-value">{{
                      formatDate(doc.dateExpired)
                    }}</span>
                  </span>
                } @else {
                  <span class="ged-picker__expiry">
                    <span class="ged-picker__expiry-label">Expiration</span>
                    <span class="ged-picker__expiry-value is-missing"
                      >non renseignée</span
                    >
                  </span>
                }

                <span class="ged-picker__actions">
                  <button
                    type="button"
                    class="ged-picker__icon-btn"
                    [title]="previewButtonTitle(doc)"
                    [attr.aria-label]="
                      previewButtonTitle(doc) + ' — ' + label(doc)
                    "
                    [disabled]="hasNoFile(doc)"
                    (click)="onRowClick(doc); $event.stopPropagation()"
                  >
                    <pr-icon
                      [name]="
                        unavailableAfterTry(doc) ? 'refresh' : 'visibility'
                      "
                      [width]="19"
                    ></pr-icon>
                  </button>
                </span>
              </div>
            }

            @if (remainingCount > 0) {
              <button
                type="button"
                class="ged-picker__more"
                (click)="showMore()"
              >
                Afficher {{ nextPageSize }} document{{
                  nextPageSize > 1 ? 's' : ''
                }}
                de plus
                <span class="ged-picker__more-count"
                  >{{ remainingCount }} restant{{
                    remainingCount > 1 ? 's' : ''
                  }}</span
                >
              </button>
            }
          </div>
        }
      </div>

      <aside class="ged-picker__preview">
        @if (!selected) {
          <div class="ged-picker__preview-idle">
            <pr-icon name="visibility" [width]="40"></pr-icon>
            <b>Aucun document sélectionné</b>
            <span
              >Cliquez sur une ligne, ou sur l'icône œil, pour afficher le
              document ici.</span
            >
          </div>
        } @else {
          <div class="ged-picker__preview-head">
            <h5>Aperçu</h5>
            <button
              type="button"
              class="ged-picker__icon-btn"
              title="Ouvrir dans un onglet"
              aria-label="Ouvrir dans un onglet"
              [disabled]="!previewUrl"
              (click)="openInTab()"
            >
              <pr-icon name="open_in_new" [width]="19"></pr-icon>
            </button>
          </div>

          <div class="ged-picker__preview-stage">
            @if (previewLoading) {
              <pr-loader></pr-loader>
            }
            @if (previewError) {
              <div class="ged-picker__preview-error">{{ previewError }}</div>
            }
            @if (previewUrl) {
              <iframe
                [src]="previewUrl"
                width="100%"
                height="100%"
                frameborder="0"
                title="Aperçu du document"
              ></iframe>
            }
          </div>

          <div class="ged-picker__preview-foot">
            <pr-button
              display="neutral"
              size="lg"
              icon="check"
              label="Choisir ce document"
              [disabled]="!canPick(selected)"
              (onClick)="pick(selected)"
            ></pr-button>
            <span class="ged-picker__preview-note">
              @if (isUnavailable(selected)) {
                {{ unavailableReason(selected) }}
              } @else if (!previewUrl) {
                Le document doit s'afficher avant de pouvoir être rattaché.
              } @else if (hasNoOwner(selected)) {
                Sans titulaire — il sera rattaché à
                {{ stepOwnerLabel || 'ce souscripteur' }}.
              } @else {
                Rattaché à {{ ownersLabel(selected) }}.
              }
            </span>
          </div>
        }
      </aside>
    </div>

    <div class="ged-picker__foot">
      <span class="ged-picker__hint">
        Le document choisi remplace celui proposé automatiquement. Aucun fichier
        n'est dupliqué en GED.
      </span>
      <pr-button
        display="neutral"
        variant="outline"
        size="lg"
        label="Annuler"
        (onClick)="close()"
      ></pr-button>
    </div>
  </div>
</pr-modal>
